// Listbox.tsx
"use client";

import {
  Listbox as HeroListbox,
  ListboxItem as HeroListboxItem,
  ListboxSection as HeroListboxSection,
  type Selection,
} from "@heroui/react";
import type React from "react";
import { cn } from "../../lib/utils";

// Get the original component props types
export type ListboxProps<T extends object = object> = Omit<React.ComponentProps<typeof HeroListbox<T>>, "className"> & {
  className?: string;
};

export type ListboxItemProps = React.ComponentProps<typeof HeroListboxItem> & {
  className?: string;
};

export type ListboxSectionProps = React.ComponentProps<typeof HeroListboxSection> & {
  className?: string;
};

// Create the wrapper components
function ListboxWrapper<T extends object = object>(props: ListboxProps<T>) {
  const { className, classNames, itemClasses, children, ...rest } = props;
  return (
    <HeroListbox<T>
      {...rest}
      classNames={{
        base: cn(className),
        ...classNames,
      }}
      itemClasses={{
        ...itemClasses,
        selectedIcon: cn("text-primary", itemClasses?.selectedIcon),
      }}
    >
      {children}
    </HeroListbox>
  );
}

ListboxWrapper.displayName = "Listbox";

const ListboxItemWrapper = ({ className, classNames, children, ...props }: ListboxItemProps) => {
  return (
    <HeroListboxItem
      {...props}
      classNames={{
        ...classNames,
        base: cn(className),
      }}
    >
      {children}
    </HeroListboxItem>
  );
};

const ListboxSectionWrapper = ({ className, classNames, children, ...props }: ListboxSectionProps) => {
  return (
    <HeroListboxSection
      {...props}
      classNames={{
        base: cn(className),
        ...classNames,
      }}
    >
      {children}
    </HeroListboxSection>
  );
};

// Copy all static properties from the original components
Object.setPrototypeOf(ListboxWrapper, HeroListbox);
Object.setPrototypeOf(ListboxItemWrapper, HeroListboxItem);
Object.setPrototypeOf(ListboxSectionWrapper, HeroListboxSection);

// Copy static properties that HeroUI's collection system might need
Object.assign(ListboxWrapper, HeroListbox);
Object.assign(ListboxItemWrapper, HeroListboxItem);
Object.assign(ListboxSectionWrapper, HeroListboxSection);

// Ensure the components have the right displayName and type
ListboxItemWrapper.displayName = (HeroListboxItem as React.ComponentType<unknown>).displayName || "ListboxItem";
ListboxSectionWrapper.displayName =
  (HeroListboxSection as React.ComponentType<unknown>).displayName || "ListboxSection";

// Copy the $$typeof symbol if it exists (React uses this for component identification)
if ("$$typeof" in HeroListbox) {
  (ListboxWrapper as unknown as Record<string, unknown>).$$typeof = (
    HeroListbox as unknown as Record<string, unknown>
  ).$$typeof;
}
if ("$$typeof" in HeroListboxItem) {
  (ListboxItemWrapper as unknown as Record<string, unknown>).$$typeof = (
    HeroListboxItem as unknown as Record<string, unknown>
  ).$$typeof;
}
if ("$$typeof" in HeroListboxSection) {
  (ListboxSectionWrapper as unknown as Record<string, unknown>).$$typeof = (
    HeroListboxSection as unknown as Record<string, unknown>
  ).$$typeof;
}

// Export the wrapped components
export const Listbox = ListboxWrapper;
export const ListboxItem = ListboxItemWrapper;
export const ListboxSection = ListboxSectionWrapper;

// Re-export types
export type { Selection };
